import { IonCol, IonRow } from '@ionic/react';
import tubeSheetExample from '../../../../assets/images/information-images/completed-tubes-info/additional-details.png';
import tubeSheetShiftedOrigin from '../../../../assets/images/information-images/completed-tubes-info/tubes-incorrect.png';
import tubeSheetCorrect from '../../../../assets/images/information-images/completed-tubes-info/tubes-correct.png';
import React from 'react';
import { useIntl } from 'react-intl';
import { DqText } from '../../../dq-text';
import { JobMessages } from 'src/intl/JobMessages';
import './InformationModals.scss';
import { AppModal } from '../../../reusable-components/app-modal/AppModal';
import { useBreakpoint } from 'src/utils/helpers/platformHelpers';

interface TubeSheetModalProps {
  /**
   * Whether the modal is open.
   */
  isOpen: boolean;
  /**
   * Triggers the request to close the modal.
   */
  onClose: () => void;
}

export const TubeSheetModal: React.FC<TubeSheetModalProps> = ({ onClose, isOpen }) => {
  const { formatMessage } = useIntl();
  const isDesktopOrTablet = useBreakpoint('sm');
  const columnSize = isDesktopOrTablet ? 'auto' : '4';

  return (
    <AppModal
      isOpen={isOpen}
      title={formatMessage(JobMessages.tubeSheetTitle)}
      onClose={onClose}
      primaryButton={{
        label: formatMessage(JobMessages.gotItButton),
        onClick: onClose,
      }}
    >
      <IonRow className="">
        <DqText variant="body-strong"> {formatMessage(JobMessages.tubeStatusHeading)}</DqText>
      </IonRow>
      <IonRow className="">
        <DqText variant="body"> {formatMessage(JobMessages.tubeStatusKeyDetails)}</DqText>
      </IonRow>
      <IonRow>
        <IonCol size={columnSize}>
          <div className="dot green" />
          <DqText variant="body-strong">{formatMessage(JobMessages.cleanedLabel)}</DqText>
        </IonCol>

        <IonCol size={columnSize}>
          <div className="dot autostroke-green" />
          <DqText variant="body-strong">{formatMessage(JobMessages.statusAutostroke)}</DqText>
        </IonCol>

        <IonCol size={columnSize}>
          <div className="dot not-cleaned" />

          <DqText variant="body-strong">{formatMessage(JobMessages.statusNotCleaned)}</DqText>
        </IonCol>
        <IonCol size={columnSize}>
          <div className="dot blocked" />

          <DqText variant="body-strong">{formatMessage(JobMessages.statusBlocked)}</DqText>
        </IonCol>

        <IonCol size={columnSize}>
          <div className="dot skipped" />
          <DqText variant="body-strong">{formatMessage(JobMessages.statusSkipped)}</DqText>
        </IonCol>
        <IonCol size={columnSize}>
          <div className="dot" />
          <DqText variant="body-strong">{formatMessage(JobMessages.statusNone)}</DqText>
        </IonCol>
      </IonRow>
      <IonRow>
        <DqText variant="body-strong">{formatMessage(JobMessages.additionalDetailsHeading)}</DqText>
      </IonRow>
      <IonRow>
        <DqText variant="body">{formatMessage(JobMessages.tubeStatusDetails)}</DqText>
      </IonRow>
      <IonRow className="ion-padding">
        <IonCol className="ion-padding" size="6">
          <img src={tubeSheetExample} alt="tube tool tip information" className="tube-sheet-image" />
        </IonCol>
        <IonCol size="6">
          <IonRow>
            <DqText variant="body-strong"> {formatMessage(JobMessages.tubeSheetDepthTitle)}</DqText>
          </IonRow>
          <IonRow className="section-bottom">
            <DqText variant="body"> {formatMessage(JobMessages.depthCleanedDescription)}</DqText>
          </IonRow>
          <IonRow>
            <DqText variant="body-strong"> {formatMessage(JobMessages.autoStrokeWithTimeTitle)}</DqText>
          </IonRow>
          <IonRow className="section-bottom">
            <DqText variant="body"> {formatMessage(JobMessages.autostrokeInfo)}</DqText>
          </IonRow>
        </IonCol>
      </IonRow>
      <IonRow>
        <DqText variant="body-strong">{formatMessage(JobMessages.zoomHeading)}</DqText>
      </IonRow>
      <IonRow>
        <DqText variant="body">{formatMessage(JobMessages.zoomSubHeading)}</DqText>
      </IonRow>
      <IonRow>
        <ul>
          <li>
            <DqText variant="body">{formatMessage(JobMessages.zoomInstructionsBulletOne)}</DqText>
          </li>
          <li>
            <DqText variant="body">{formatMessage(JobMessages.zoomInstructionsBulletTwo)}</DqText>
          </li>

          <li>
            <DqText variant="body">{formatMessage(JobMessages.zoomInstructionsBulletThree)}</DqText>
          </li>
        </ul>
      </IonRow>
      <IonRow>
        <DqText variant="body-strong">{formatMessage(JobMessages.insightsHeading)}</DqText>
      </IonRow>
      <IonRow>
        <ul>
          <li>
            <DqText variant="body">{formatMessage(JobMessages.insightsInstructionsBulletOne)}</DqText>
          </li>
          <li>
            <DqText variant="body">{formatMessage(JobMessages.insightsInstructionsBulletTwo)}</DqText>
          </li>

          <li>
            <DqText variant="body">{formatMessage(JobMessages.insightsInstructionsBulletThree)}</DqText>
          </li>
        </ul>
      </IonRow>
      <IonRow className="section-bottom">
        <DqText variant="body">{formatMessage(JobMessages.insightsAdditionalInformation)}</DqText>
      </IonRow>
      <IonRow className="heading-row">
        <DqText variant="body-strong"> {formatMessage(JobMessages.graphingIssuesHeading)}</DqText>
      </IonRow>
      <IonRow className="issues-row">
        <DqText variant="body">{formatMessage(JobMessages.graphingIssuesSubHeading)}</DqText>
      </IonRow>
      <ul>
        <li>
          <DqText variant="body">{formatMessage(JobMessages.issuesInfoBulletOne)}</DqText>
        </li>
        <li>
          <DqText variant="body">{formatMessage(JobMessages.issuesInfoBulletTwo)}</DqText>
        </li>

        <li>
          <DqText variant="body">{formatMessage(JobMessages.issuesInfoBulletThree)}</DqText>
        </li>
      </ul>

      <IonRow>
        <DqText variant="body">{formatMessage(JobMessages.graphingIssuesSuggestion)}</DqText>
      </IonRow>
      <IonRow className="ion-padding ion-justify-content-center">
        <IonCol>
          <DqText variant="body-strong">{formatMessage(JobMessages.correctTubeSheetLabel)}</DqText>
          <img src={tubeSheetCorrect} alt="tube tool tip information" />
        </IonCol>
        <IonCol>
          {' '}
          <DqText variant="body-strong">{formatMessage(JobMessages.shiftedOriginTubeSheetLabel)}</DqText>
          <img src={tubeSheetShiftedOrigin} alt="tube tool tip information" />
        </IonCol>
      </IonRow>
    </AppModal>
  );
};

export default TubeSheetModal;
